import { type MatchContext, pickVariant } from "@/lib/targeting/match";
import type { MessageSlot, MessageVariant } from "@/lib/widget-config";

export interface WelcomePick {
  slotId: string;
  kind: MessageSlot["kind"];
  variant: MessageVariant;
}

/** slotId -> the variant shown at the first reveal (`null` = nothing matched then). */
export type FrozenPicks = Map<string, MessageVariant | null>;

/**
 * Chooses what each welcome slot shows. With `messageSlotId` (a `message:<slotId>` thread) only that
 * MESSAGE slot is shown. SYSTEM re-picks on every call (the host navigated);
 * MESSAGE and HINT are frozen per thread at their first reveal (`freeze`) so a bubble the visitor
 * already read never changes under them. While `hold` is set (session content pending) MESSAGE and
 * HINT are withheld and NOT frozen, so the first reveal never shows "Hi there" then "Hi Olena".
 */
export function resolveWelcomes(
  slots: readonly MessageSlot[],
  ctx: MatchContext,
  frozen: FrozenPicks,
  freeze: boolean,
  opts: { hold?: boolean; messageSlotId?: string; excludeMessages?: boolean } = {},
): WelcomePick[] {
  const out: WelcomePick[] = [];
  for (const slot of slots) {
    let variant: MessageVariant | null;
    // Contract v1: MESSAGE slots live in their own `message:<slotId>` threads, never in another thread.
    if (opts.excludeMessages && slot.kind === "MESSAGE") continue;
    // A `message:<slotId>` thread shows its own slot only; other MESSAGE slots belong to other threads.
    if (opts.messageSlotId && slot.kind === "MESSAGE" && slot.id !== opts.messageSlotId) continue;
    if (slot.kind === "SYSTEM") {
      variant = pickVariant(slot.variants, ctx);
    } else if (frozen.has(slot.id)) {
      // The variant that was picked stays; its CURRENT content shows (a config refresh, an edit in the playground).
      const picked = frozen.get(slot.id) ?? null;
      variant = picked ? (slot.variants.find((x) => x.id === picked.id) ?? picked) : null;
    } else if (opts.hold) {
      continue;
    } else {
      variant = pickVariant(slot.variants, ctx);
      if (freeze) frozen.set(slot.id, variant);
    }
    if (variant) out.push({ slotId: slot.id, kind: slot.kind, variant });
  }
  return out;
}
